import React, { useEffect, useMemo, useRef, useState } from "react";
import { withStyles } from "@material-ui/core/styles";
import compose from "recompose/compose";
import { useSelector } from "react-redux";
import TrackChangesIcon from "@material-ui/icons/TrackChanges";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const LPB_START_KEY = "lpb_event_start";
const LPB_END_KEY = "lpb_event_end";

function LinearProgressBar(props) {
  const {
    classes,
    targetedValue,
    maximumValue,
    completedActivity,
    showKm,
    kmsCompleted,
    challengeParameter,
  } = props;

  const userSelectedEvent = useSelector(
    (state) => state.user.userSelectedEvent
  );

  const progressRef = useRef(null);
  const containerRef = useRef(null);

  const [barWidth, setBarWidth] = useState(0);
  const [openWeek, setOpenWeek] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const SHOW_WEEK_DISTRIBUTION =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/eventDetails");

  const toNumber = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string") {
      const n = Number(value.trim());
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };
  const parseDate = (dateLike) => {
    if (!dateLike) return null;

    if (dateLike instanceof Date) {
      const t = dateLike.getTime();
      return Number.isNaN(t) ? null : new Date(t);
    }

    if (typeof dateLike === "number") {
      const d = new Date(dateLike);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    if (typeof dateLike === "string") {
      const s = dateLike.trim();
      if (!s) return null;

      //  IMPORTANT: treat "YYYY-MM-DD" as LOCAL date (avoid UTC shift)
      const isoLocal = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
      if (isoLocal) {
        const y = Number(isoLocal[1]);
        const m = Number(isoLocal[2]) - 1;
        const d = Number(isoLocal[3]);
        const dt = new Date(y, m, d);
        return Number.isNaN(dt.getTime()) ? null : dt;
      }

      // DD-MM-YYYY or DD/MM/YYYY
      const m = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})(?:\s.*)?$/);
      if (m) {
        const day = Number(m[1]);
        const month = Number(m[2]) - 1;
        const year = Number(m[3]);
        const d = new Date(year, month, day);
        return Number.isNaN(d.getTime()) ? null : d;
      }

      const d = new Date(s);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const d = new Date(dateLike);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const toStartOfDay = (dateLike) => {
    const d = parseDate(dateLike);
    if (!d) return null;

    const copy = new Date(d.getTime());
    copy.setHours(0, 0, 0, 0);
    return copy;
  };

  const reduxStartRaw = userSelectedEvent?.localStartDate;
  const reduxEndRaw = userSelectedEvent?.localEndDate;

  // Fallbacks already being set by EventDetails
  const lsStartRaw =
    typeof window !== "undefined" ? localStorage.getItem("start") : null; // "DD-MM-YYYY"
  const lsEndRaw =
    typeof window !== "undefined" ? localStorage.getItem("end") : null; // "DD-MM-YYYY"
  const lsDaysRaw =
    typeof window !== "undefined" ? localStorage.getItem("days") : null;

  const persistedStartRaw =
    typeof window !== "undefined" ? localStorage.getItem(LPB_START_KEY) : null;

  const persistedEndRaw =
    typeof window !== "undefined" ? localStorage.getItem(LPB_END_KEY) : null;

  // EventDetails sometimes stores "false" as string in localStorage.end
  const cleanedLsEndRaw =
    lsEndRaw && !["false", "null", "undefined", ""].includes(lsEndRaw)
      ? lsEndRaw
      : null;

  let eventStartDate =
    parseDate(reduxStartRaw) ||
    parseDate(lsStartRaw) ||
    parseDate(persistedStartRaw);

  let eventEndDate =
    parseDate(reduxEndRaw) ||
    parseDate(cleanedLsEndRaw) ||
    parseDate(persistedEndRaw);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (reduxStartRaw)
      localStorage.setItem(LPB_START_KEY, String(reduxStartRaw));
    if (reduxEndRaw) localStorage.setItem(LPB_END_KEY, String(reduxEndRaw));
  }, [reduxStartRaw, reduxEndRaw]);

  // If end missing but we know days, compute end from start
  const daysNum = lsDaysRaw ? Number(lsDaysRaw) : null;
  if (
    !eventEndDate &&
    eventStartDate &&
    Number.isFinite(daysNum) &&
    daysNum > 0
  ) {
    const end = new Date(eventStartDate.getTime());
    end.setDate(end.getDate() + (daysNum - 1));
    eventEndDate = end;
  }

  // If end < start (bad data), clamp so weeks still render
  if (
    eventStartDate &&
    eventEndDate &&
    eventEndDate.getTime() < eventStartDate.getTime()
  ) {
    eventEndDate = new Date(eventStartDate.getTime());
  }

  useEffect(() => {
    if (!progressRef.current) return;

    const el = progressRef.current;

    const update = () => {
      const w = el.getBoundingClientRect().width;
      setBarWidth(w || 0);
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mql = window.matchMedia("(max-width: 600px)");
    const update = () => setIsMobile(mql.matches);

    update();

    if (mql.addEventListener) mql.addEventListener("change", update);
    else mql.addListener(update);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", update);
      else mql.removeListener(update);
    };
  }, []);

  const targetNum = Number(targetedValue);
  const safeTarget = Number.isFinite(targetNum) ? targetNum : 0;

  const doneNum =
    challengeParameter === "DISTANCE"
      ? Number(kmsCompleted ?? completedActivity)
      : Number(completedActivity);

  const safeDone = Number.isFinite(doneNum) ? doneNum : 0;

  const computedPercent =
    safeTarget > 0 ? Math.min(100, (safeDone / safeTarget) * 100) : 0;

  const computedIsMaxThanTargeted = safeTarget > 0 && safeDone >= safeTarget;

  const formatProgressbarValue = (number) => {
    const str = String(number);
    const [integerPart, decimalPartRaw] = str.split(".");
    const decimalPart = decimalPartRaw ? `.${decimalPartRaw}` : "";

    const formattedIntegerPart = integerPart.replace(
      /(\d)(?=(\d\d)+\d$)/g,
      "$1,"
    );

    return formattedIntegerPart + decimalPart;
  };

  const formatWeekTarget = (value) => {
    if (value === null || value === undefined) return "0";

    const num = Number(value);
    if (!Number.isFinite(num)) return "0";

    if (challengeParameter === "DISTANCE") {
      const fixed = num >= 100 ? num.toFixed(1) : num.toFixed(2);
      return fixed.replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
    }

    const rounded = Math.round(num);
    return rounded >= 10000 ? formatProgressbarValue(rounded) : String(rounded);
  };

  const getBubbleTransformX = (leftPercent, isLast = false) => {
    if (isLast) return "-92%";
    if (leftPercent <= 10) return "-4%";
    if (leftPercent >= 90) return "-84%";
    return "-50%";
  };

  const getWeeks = (startDateLike, endDateLike, totalTarget) => {
    const start = toStartOfDay(startDateLike);
    const end = toStartOfDay(endDateLike);
    if (!start || !end) return [];

    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return [];

    const totalDays = Math.floor(diffMs / MS_PER_DAY) + 1;
    if (totalDays <= 0) return [];

    const totalTargetNum = toNumber(totalTarget);

    const weeks = [];
    let remainingDays = totalDays;
    let consumedDays = 0;
    let weekNumber = 1;

    while (remainingDays > 0) {
      const daysInWeek = Math.min(7, remainingDays);
      consumedDays += daysInWeek;

      const leftPercent = (consumedDays / totalDays) * 100;

      //cumulative addition for week target
      const weekTarget =
        totalTargetNum !== null
          ? (totalTargetNum * consumedDays) / totalDays
          : null;

      // Or, if you want per-week target instead of cumulative:
      // const weekTarget =
      //   totalTargetNum !== null
      //     ? (totalTargetNum * daysInWeek) / totalDays
      //     : null;

      weeks.push({
        weekNumber,
        daysInWeek,
        leftPercent,
        weekTarget,
      });

      remainingDays -= daysInWeek;
      weekNumber += 1;
    }

    return weeks;
  };

  const adjustMarkerPercents = (weeks, barWidthPx, isMobileView) => {
    if (!weeks?.length) return [];
    if (!barWidthPx) return weeks;

    const iconPx = 18;
    const r = iconPx / 2;

    // keep markers inside the bar on both mobile and desktop
    const capPercent = isMobileView ? 98 : 99.1;

    const maxX = Math.min((capPercent / 100) * barWidthPx, barWidthPx - r);
    const minX = r;

    const desiredGap = isMobileView ? 26 : 22;

    const n = weeks.length;
    const maxPossibleGap = n > 1 ? (maxX - minX) / (n - 1) : desiredGap;
    const gap = Math.min(desiredGap, maxPossibleGap);

    const xs = weeks.map((w) => {
      const x = (w.leftPercent / 100) * barWidthPx;
      return Math.max(minX, Math.min(x, maxX));
    });

    xs[n - 1] = Math.min(xs[n - 1], maxX);

    for (let i = n - 2; i >= 0; i--) {
      xs[i] = Math.min(xs[i], xs[i + 1] - gap);
    }

    if (xs[0] < minX) {
      xs[0] = minX;
      for (let i = 1; i < n; i++) {
        xs[i] = Math.max(xs[i], xs[i - 1] + gap);
      }
      xs[n - 1] = Math.min(xs[n - 1], maxX);
    }

    return weeks.map((w, i) => ({
      ...w,
      leftPercent: (xs[i] / barWidthPx) * 100,
    }));
  };

  const unit = challengeParameter === "DISTANCE" ? "Km" : "Steps";

  const weeks = useMemo(() => {
    if (!SHOW_WEEK_DISTRIBUTION) return [];
    return getWeeks(eventStartDate, eventEndDate, targetedValue);
  }, [SHOW_WEEK_DISTRIBUTION, eventStartDate, eventEndDate, targetedValue]);

  const weekMarkers = useMemo(
    () => adjustMarkerPercents(weeks, barWidth, isMobile),
    [weeks, barWidth, isMobile]
  );

  const progressStyle = useMemo(
    () => ({
      opacity: 1,
      width: computedIsMaxThanTargeted ? "100%" : `${computedPercent}%`,
      background: "linear-gradient(90deg, #36729eff 0%, #0B3F63 100%)",
      borderRadius: "20px",
    }),
    [computedPercent, computedIsMaxThanTargeted]
  );

  const completedValueText =
    challengeParameter === "DISTANCE"
      ? safeDone.toFixed(2).replace(/\.00$/, "")
      : formatProgressbarValue(Math.round(safeDone));

  const completedLabel = `${completedValueText} ${unit} Completed`;

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!containerRef.current) return;

      // If click is outside the whole progressbar container - close bubble
      if (!containerRef.current.contains(e.target)) {
        setOpenWeek(null);
      }
    };

    // Use capture phase so it still works even if some child stops propagation
    document.addEventListener("mousedown", handleOutsideClick, true);
    document.addEventListener("touchstart", handleOutsideClick, true);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick, true);
      document.removeEventListener("touchstart", handleOutsideClick, true);
    };
  }, []);

  return (
    <div className={classes.progressBar} ref={containerRef}>
      <div className={classes.progress} ref={progressRef}>
        {/* Always render exactly ONE filled bar */}
        <div className={classes.progressDone} style={progressStyle} />

        {SHOW_WEEK_DISTRIBUTION && weekMarkers.length > 0 && (
          <div className={classes.weekMarkerLayer}>
            {weekMarkers.map((w, idx) => {
              let left = `${w.leftPercent}%`;
              if (w.leftPercent <= 5) left = "3%";
              const isLast = idx === weekMarkers.length - 1;
              const translateX = getBubbleTransformX(w.leftPercent, isLast);
              const isFront = openWeek === w.weekNumber;

              const arrowPosStyle = {
                left:
                  w.leftPercent <= 10
                    ? "14px"
                    : isLast || w.leftPercent >= 90
                    ? "calc(100% - 14px)"
                    : "50%",
                transform:
                  w.leftPercent <= 10
                    ? "translateX(-50%)"
                    : isLast
                    ? "translateX(-40%)"
                    : w.leftPercent >= 90
                    ? "translateX(-100%)"
                    : "translateX(-50%)",
              };

              const bubble = (
                <div
                  className={classes.weekBubble}
                  style={{
                    left,
                    transform: `translate(${translateX}, -100%)`,
                    zIndex: isFront ? 9999 : 7,
                  }}
                  title={`Week ${w.weekNumber}`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenWeek(w.weekNumber);
                  }}
                >
                  <div className={classes.weekBubbleTitle}>
                    Week {w.weekNumber}
                  </div>
                  <div className={classes.weekBubbleSub}>
                    Target: {formatWeekTarget(w.weekTarget)} {unit}
                  </div>
                  <div
                    className={classes.weekBubbleArrow}
                    style={arrowPosStyle}
                  />
                </div>
              );

              return (
                <React.Fragment key={`wk-${w.weekNumber}`}>
                  <button
                    type="button"
                    className={classes.targetIcon}
                    style={{ left }}
                    title={`Week ${w.weekNumber}`}
                    aria-label={`Week ${w.weekNumber} target`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenWeek((prev) =>
                        prev === w.weekNumber ? null : w.weekNumber
                      );
                    }}
                  >
                    <TrackChangesIcon className={classes.targetIconSvg} />
                  </button>

                  {openWeek === w.weekNumber && bubble}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      <div className={classes.valueLayout}>
        {!computedIsMaxThanTargeted && (
          <div
            style={{
              flex: "0 0 auto",
              fontSize: "20px",
              whiteSpace: "nowrap",
              overflow: "visible",
              maxWidth: "100%",
            }}
          >
            <span>
              {showKm
                ? completedLabel
                : `${Math.round(computedPercent)}% Completed`}
            </span>
          </div>
        )}

        <div
          className={classes.targetedValue}
          style={{
            // flex: "1 1 auto",
            minWidth: 0,
            paddingLeft: "30px",
            textAlign: "right",
          }}
        >
          <span
            style={{
              marginLeft:
                maximumValue > 1 && maximumValue <= 15 ? "10px" : undefined,
            }}
          >
            {targetedValue && targetedValue >= 10000
              ? formatProgressbarValue(targetedValue)
              : targetedValue && targetedValue < 10000
              ? targetedValue
              : "0"}{" "}
            {unit}
          </span>

          <br />

          {maximumValue && (
            <p
              style={{
                margin: 0,
                fontSize: "15px",
                width: "120px",
                textAlign: "left",
              }}
            >
              Total target
            </p>
          )}
        </div>

        {computedIsMaxThanTargeted && (
          <div
            className={classes.targetedValue}
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              marginTop: maximumValue ? 0 : "5px",
              textAlign: "right",
            }}
          >
            <span>
              {completedValueText} {unit}
            </span>

            {maximumValue && (
              <p style={{ margin: 0, fontSize: "15px" }}>Achieved</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = () => ({
  progressBar: {
    width: "100%",
    marginTop: "30px",
    marginBottom: "30px",
    paddingTop: 50,
    boxSizing: "border-box",
  },
  progress: {
    backgroundColor: "#E3E9F0",
    borderRadius: "20px",
    height: "30px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    position: "relative",
    overflow: "visible",
    border: "1px solid rgba(15, 23, 42, 0.16)",
    boxShadow:
      "inset 0 1px 2px rgba(15, 23, 42, 0.12), inset -1px 0 2px rgba(15, 23, 42, 0.08)",
  },
  progressDone: {
    boxShadow:
      "0 2px 8px rgba(15, 23, 42, 0.12), inset -1px 0 0 rgba(255,255,255,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    transition: "1s ease-in",
  },
  valueLayout: {
    width: "100%",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    color: "#a52a2a",
    fontWeight: "bold",
    marginTop: "5px",
  },
  targetedValue: {
    fontSize: "20px",
    textAlign: "end",
  },
  weekMarkerLayer: {
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
    zIndex: 5,
    pointerEvents: "none",
  },
  weekBubble: {
    position: "absolute",
    top: "-12px",
    backgroundColor: "#09456c",
    color: "#fff",
    padding: "10px 12px",
    borderRadius: "10px",
    fontSize: "12px",
    lineHeight: 1.3,
    whiteSpace: "nowrap",
    boxShadow: "0 10px 18px rgba(0,0,0,0.22)",
    pointerEvents: "auto",
    border: "1px solid rgba(255,255,255,0.25)",
    cursor: "pointer",
  },
  weekBubbleTitle: {
    fontWeight: 800,
    fontSize: "12px",
    marginBottom: "3px",
    color: "#fff",
    letterSpacing: "0.2px",
  },
  weekBubbleSub: {
    fontWeight: 700,
    fontSize: "12px",
    color: "#CDE7FF",
  },
  weekBubbleArrow: {
    position: "absolute",
    bottom: "-8px",
    width: 0,
    height: 0,
    borderLeft: "8px solid transparent",
    borderRight: "8px solid transparent",
    borderTop: "8px solid #09456c",
  },
  targetIcon: {
    pointerEvents: "auto",
    position: "absolute",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: 18,
    height: 18,
    borderRadius: "999px",
    background: "#FFFFFF",
    border: "1px solid rgba(15, 23, 42, 0.18)",
    boxShadow: "0 2px 7px rgba(15, 23, 42, 0.14)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    zIndex: 6,
  },
  targetIconSvg: {
    fontSize: 13,
    color: "#2E6E9C",
  },
});

export default compose(withStyles(styles, { withTheme: true }))(
  LinearProgressBar
);
